import { styled } from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetElementSize } from '../../hooks/useGetElementSize';
import { useIndexRun } from '../../hooks/useIndexRun';
import FilesView from '../../pages/Files';
import { getFolder, getFolders } from '../../redux/actions/folderActions';
import { myFolder } from '../../redux/consts';
import { selectAppStatus, setCurrentApp } from '../../redux/reducers/appStatusReducer';
import { selectFolders, setCurrentFolder } from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { StyledChatContainer } from '../../theme/CustomComponents';
import {
  AppEnum,
  DealGPTView,
  FilesTabs,
  FolderView,
  IndexType,
  ProjectConfigView,
} from '../../Types/enums';
import { Folder as FolderType } from '../../Types/folder';
import Chat from '../Chat/Chat';
import ProjectStatusButton from '../DealGPT/PhaseTwo/ProjectStatusButton';
import DealGPTConfigureProjectContainer from '../DealGPT/ViewModels/DealGPTConfigureProjectContainer';
import DealGPTProject from '../DealGPT/ViewModels/DealGPTProject';
import IndexStatus from '../Files/IndexStatus';
import LoadingMask from '../LoadingMask';
import { useAuth } from '../Providers/AuthProvider';
import Chats from './Chats';
import EditFolder from './EditFolder/EditFolder';
import FolderHeader from './FolderHeader';
import PromptView from './Prompts';

const TitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const Folder = ({
  selectedView,
  configView,
  filesView,
}: {
  selectedView: FolderView;
  configView?: ProjectConfigView;
  filesView?: FilesTabs;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { selectedApp } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);
  const { folderId } = useParams();
  const { isAdmin } = useAuth();
  const { latestIndexRunValues } = useIndexRun(currentFolder?.indexIds?.[0]);

  const { height: statusHeight } = useGetElementSize('project-indexed-status');

  useEffect(() => {
    if (folderId !== currentFolder?.id) {
      let foldersWithProjects = selectedApp === AppEnum.DEAL_GPT;
      if (folderId && folderId !== myFolder.id) {
        dispatch(getFolder({ folderId, isAdmin })).then(
          (action: PayloadAction<FolderType | unknown>) => {
            const retFolder = action.payload as FolderType;
            if (!retFolder.id) {
              dispatch(setCurrentFolder(myFolder));
              navigate(`/folder/${myFolder.id}`);
              return;
            }

            if (
              (retFolder.projectId && selectedApp !== AppEnum.DEAL_GPT) ||
              (!retFolder.projectId && selectedApp === AppEnum.DEAL_GPT)
            ) {
              if (retFolder.projectId) {
                dispatch(setCurrentApp(AppEnum.DEAL_GPT));
                foldersWithProjects = true;
              } else {
                foldersWithProjects = false;
              }

              dispatch(getFolders({ foldersWithProjects }));
            }
          }
        );
      } else {
        dispatch(setCurrentFolder(myFolder));
      }
    }
  }, [dispatch, navigate, currentFolder, folderId, selectedApp, isAdmin]);

  useEffect(() => {
    if (
      selectedView === FolderView.DATA_PROCESSING &&
      currentFolder?.id &&
      currentFolder.project?.indexToUse === IndexType.GRAPH_RAG
    ) {
      navigate(`/folder/${currentFolder.id}/discovery`, { replace: true });
    }
  }, [currentFolder?.id, currentFolder?.project?.indexToUse, navigate, selectedView]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <StyledChatContainer style={{ padding: '0px 0px 0px 20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            paddingTop: '20px',
            gap: '20px',
            width: 'inherit',
          }}
        >
          {currentFolder?.project && (
            <TitleContainer id="project-indexed-status">
              <IndexStatus />
            </TitleContainer>
          )}
          <FolderHeader folderView={selectedView} />
          <div
            style={{
              overflowY: 'hidden',
              height: `calc(100vh - 150px - ${statusHeight}px - var(--banner-height, 0px))`,
            }}
          >
            {currentFolder?.project &&
              FolderView.EDIT !== selectedView &&
              [IndexType.OPENAI, IndexType.GRAPH_RAG].includes(currentFolder.project.indexToUse) &&
              !latestIndexRunValues?.lastFileIndexRunTime &&
              selectedView !== FolderView.FILES && <LoadingMask isLoading={null} />}
            {selectedView === FolderView.CHATS && <Chats />}
            {selectedView === FolderView.CHAT &&
              (currentFolder?.projectId ? (
                <DealGPTProject
                  selectedView={DealGPTView.CHAT}
                  projectId={currentFolder.projectId}
                />
              ) : (
                <Chat />
              ))}
            {selectedView === FolderView.DISCOVERY && currentFolder?.projectId && (
              <DealGPTProject
                selectedView={DealGPTView.DISCOVERY}
                projectId={currentFolder.projectId}
              />
            )}
            {selectedView === FolderView.DATA_PROCESSING && currentFolder?.projectId && (
              <DealGPTProject
                selectedView={DealGPTView.DATA_PROCESSING}
                projectId={currentFolder.projectId}
              />
            )}
            {selectedView === FolderView.DATA_REQUEST_LIST && currentFolder?.projectId && (
              <DealGPTProject
                selectedView={DealGPTView.DATA_REQUEST_LIST}
                projectId={currentFolder.projectId}
              />
            )}
            {selectedView === FolderView.ANALYSIS && currentFolder?.projectId && (
              <DealGPTProject
                selectedView={DealGPTView.ANALYSIS}
                projectId={currentFolder.projectId}
              />
            )}
            {selectedView === FolderView.SCOPE_AREAS && currentFolder?.projectId && (
              <DealGPTProject
                selectedView={DealGPTView.SCOPE_AREAS}
                projectId={currentFolder.projectId}
              />
            )}
            {selectedView === FolderView.PROMPTS && <PromptView isAIAppStore={false} />}
            {selectedView === FolderView.EDIT && <EditFolder folder={currentFolder} />}
            {selectedView === FolderView.CONFIG && configView && (
              <DealGPTConfigureProjectContainer folder={currentFolder} selectedView={configView} />
            )}
            {selectedView === FolderView.FILES && <FilesView selectedView={filesView} />}
          </div>
          {currentFolder?.projectId && currentFolder.project?.name && (
            <ProjectStatusButton
              selectedView={selectedView}
              projectId={currentFolder.projectId}
              projectName={currentFolder.project?.name}
            />
          )}
        </div>
      </StyledChatContainer>
    </div>
  );
};

export default Folder;
